/**
 * BroadcastChannel Hook for Cross-Window Communication
 *
 * Enables communication between the main app window and popup session windows.
 * Used to sync session state across windows.
 */

import { useEffect, useRef, useCallback } from 'react';

// Channel name for session state sync
const CHANNEL_NAME = 'demopath-session';

// Message types
export type SessionBroadcastType =
  | 'SESSION_START'
  | 'SESSION_END'
  | 'SESSION_RESET'
  | 'STATE_UPDATE'
  | 'TRANSCRIPT_UPDATE'
  | 'COVERAGE_UPDATE'
  | 'POPUP_READY'
  | 'POPUP_ACTION';

export interface SessionBroadcastMessage {
  type: SessionBroadcastType;
  payload?: unknown;
  timestamp: number;
  source: 'main' | 'popup';
}

interface UseBroadcastChannelOptions {
  onMessage?: (message: SessionBroadcastMessage) => void;
  source: 'main' | 'popup';
}

/**
 * Hook for broadcast channel communication between windows
 */
export function useBroadcastChannel(options: UseBroadcastChannelOptions) {
  const { onMessage, source } = options;
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Initialize channel
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('BroadcastChannel not supported in this browser');
      return;
    }

    channelRef.current = new BroadcastChannel(CHANNEL_NAME);

    channelRef.current.onmessage = (event: MessageEvent<SessionBroadcastMessage>) => {
      // Ignore messages from same source
      if (event.data.source === source) return;
      onMessage?.(event.data);
    };

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [onMessage, source]);

  // Send message
  const broadcast = useCallback((type: SessionBroadcastType, payload?: unknown) => {
    if (!channelRef.current) return;

    const message: SessionBroadcastMessage = {
      type,
      payload,
      timestamp: Date.now(),
      source,
    };

    channelRef.current.postMessage(message);
  }, [source]);

  return { broadcast };
}

// Track the actual popup window reference
let popupWindowRef: Window | null = null;

/**
 * Check if a session popup is currently open
 * Verifies both the flag AND that the window is actually open
 */
export function isPopupOpen(): boolean {
  const flagSet = sessionStorage.getItem('demopath-popup-open') === 'true';

  // If flag is set but window is closed or doesn't exist, clear the stale flag
  if (flagSet && (!popupWindowRef || popupWindowRef.closed)) {
    console.log('🧹 Clearing stale popup flag');
    sessionStorage.removeItem('demopath-popup-open');
    popupWindowRef = null;
    return false;
  }

  return flagSet && popupWindowRef !== null && !popupWindowRef.closed;
}

/**
 * Mark popup as open/closed
 */
export function setPopupOpen(open: boolean): void {
  if (open) {
    sessionStorage.setItem('demopath-popup-open', 'true');
  } else {
    sessionStorage.removeItem('demopath-popup-open');
    popupWindowRef = null;
  }
}

/**
 * Clear any stale popup state (call on app initialization)
 */
export function clearStalePopupState(): void {
  // If we have a flag but no window reference, it's stale
  if (sessionStorage.getItem('demopath-popup-open') === 'true' && !popupWindowRef) {
    console.log('🧹 Clearing stale popup state on init');
    sessionStorage.removeItem('demopath-popup-open');
  }
}

/**
 * Open session popup window
 */
export function openSessionPopup(): Window | null {
  // Clear any stale state first
  clearStalePopupState();

  // Check if already open (with verification)
  if (isPopupOpen()) {
    console.warn('Session popup is already open');
    // Try to focus the existing popup
    popupWindowRef?.focus();
    return popupWindowRef;
  }

  const width = 450;
  const height = 700;
  const left = window.screen.width - width - 50;
  const top = 50;

  const popup = window.open(
    '/session-popup',
    'demopath-session',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );

  if (popup) {
    popupWindowRef = popup;
    setPopupOpen(true);

    // Listen for popup close
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        setPopupOpen(false);
        popupWindowRef = null;
        clearInterval(checkClosed);
      }
    }, 1000);
  } else {
    console.warn('Popup blocked - please allow popups for this site');
  }

  return popup;
}
